import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  ChevronUp, 
  Building, 
  MapPin, 
  Calendar,
  Users,
  Package,
  AlertTriangle,
  RefreshCw,
  Download,
  Settings
} from 'lucide-react';

interface Project {
  id: string;
  name: string;
  acc_project_id: string;
  hub_id: string;
  hub_name: string;
  country: string;
  status: string;
  data_centre: string;
  added_at: string;
  acc_created_at: string | null;
}

const AdminProjects: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [hubFilter, setHubFilter] = useState<string>('all');
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'name', label: 'Project Name', visible: true, sortable: true, width: 'w-48' },
    { key: 'acc_project_id', label: 'Project ID', visible: true, sortable: true, width: 'w-32' },
    { key: 'hub_name', label: 'Hub', visible: true, sortable: true, width: 'w-48' },
    { key: 'data_centre', label: 'Data Centre', visible: true, sortable: true, width: 'w-32' },
    { key: 'country', label: 'Country', visible: true, sortable: true, width: 'w-32' },
    { key: 'status', label: 'Status', visible: true, sortable: true, width: 'w-24' },
    { key: 'added_at', label: 'Added to App At', visible: true, sortable: true, width: 'w-32' },
    { key: 'acc_created_at', label: 'ACC Created At', visible: true, sortable: true, width: 'w-32' },
  ]);

  // Fetch projects data
  const { data: projectsData, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-projects'],
    queryFn: async () => {
      const data = await apiRequest('/api/admin/projects');
      return data;
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  const projects: Project[] = projectsData?.projects || [];

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           project.acc_project_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           project.hub_name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesCountry = countryFilter === 'all' || project.country === countryFilter;
      const matchesHub = hubFilter === 'all' || project.hub_name === hubFilter;

      return matchesSearch && matchesStatus && matchesCountry && matchesHub;
    });
  }, [projects, searchQuery, statusFilter, countryFilter, hubFilter]);

  // Get unique values for filters
  const uniqueCountries = useMemo(() => {
    const countries = [...new Set(projects.map(p => p.country).filter(Boolean))];
    return countries.sort();
  }, [projects]);

  const uniqueHubs = useMemo(() => {
    const hubs = [...new Set(projects.map(p => p.hub_name).filter(Boolean))];
    return hubs.sort();
  }, [projects]);

  const getStatusColor = (status: string) => {
    const statusColors = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'archived': 'bg-yellow-100 text-yellow-800',
      'suspended': 'bg-red-100 text-red-800',
    };
    return statusColors[status.toLowerCase() as keyof typeof statusColors] || 'bg-gray-100 text-gray-800';
  };

  const exportToCSV = () => {
    const headers = ['Project Name', 'Project ID', 'Hub', 'Country', 'Status', 'Created', 'Updated'];
    const csvContent = [
      headers.join(','),
      ...filteredProjects.map(project => [
        project.name,
        project.acc_project_id,
        project.hub_name,
        project.country,
        project.status,
        project.created_at,
        project.updated_at,
      ].map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `projects_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
          <span className="ml-2">Loading projects...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Failed to load projects. Please try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Projects</h1>
          <p className="text-gray-600">Manage and monitor all projects in the system ({filteredProjects.length} projects)</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className="flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <ColumnConfigDropdown 
            columns={columns}
            onColumnsChange={setColumns}
          />
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      {filtersExpanded && (
        <Card>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search projects, IDs, or hubs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={countryFilter} onValueChange={setCountryFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {uniqueCountries.map(country => (
                      <SelectItem key={country} value={country}>{country}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={hubFilter} onValueChange={setHubFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Hub" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Hubs</SelectItem>
                    {uniqueHubs.map(hub => (
                      <SelectItem key={hub} value={hub}>{hub}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {(searchQuery || statusFilter !== 'all' || countryFilter !== 'all' || hubFilter !== 'all') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSearchQuery('');
                      setStatusFilter('all');
                      setCountryFilter('all');
                      setHubFilter('all');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects Table */}
      <Card>
        <CardContent>
          {filteredProjects.length === 0 ? (
            <div className="text-center py-8">
              <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No projects found</h3>
              <p className="text-muted-foreground mb-4">
                {projects.length === 0 
                  ? "No projects are available in the system."
                  : "No projects match your current filters."
                }
              </p>
              {(searchQuery || statusFilter !== 'all' || countryFilter !== 'all' || hubFilter !== 'all') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setStatusFilter('all');
                    setCountryFilter('all');
                    setHubFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.filter(col => col.visible).map((column) => (
                      <TableHead key={column.key} className={column.width}>
                        <div className="flex items-center space-x-1">
                          <span>{column.label}</span>
                          {column.sortable && (
                            <ChevronUp className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map((project) => (
                    <TableRow key={project.id}>
                      {columns.filter(col => col.visible).map((column) => {
                        switch (column.key) {
                          case 'name':
                            return (
                              <TableCell key={column.key} className="font-medium">
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                                  {project.name}
                                </div>
                              </TableCell>
                            );
                          case 'acc_project_id':
                            return (
                              <TableCell key={column.key} className="font-mono text-sm">
                                {project.acc_project_id}
                              </TableCell>
                            );
                          case 'hub_name':
                            return (
                              <TableCell key={column.key}>
                                <div className="flex items-center">
                                  <Building className="h-4 w-4 mr-2 text-muted-foreground" />
                                  {project.hub_name}
                                </div>
                              </TableCell>
                            );
                          case 'country':
                            return (
                              <TableCell key={column.key}>
                                <div className="flex items-center">
                                  <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                                  {project.country || 'N/A'}
                                </div>
                              </TableCell>
                            );
                          case 'status':
                            return (
                              <TableCell key={column.key}>
                                <Badge className={getStatusColor(project.status)}>
                                  {project.status}
                                </Badge>
                              </TableCell>
                            );
                          case 'added_at':
                            return (
                              <TableCell key={column.key}>
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Calendar className="h-4 w-4 mr-2" />
                                  {formatDate(project.added_at)}
                                </div>
                              </TableCell>
                            );
                          case 'acc_created_at':
                            return (
                              <TableCell key={column.key}>
                                <div className="flex items-center text-sm text-muted-foreground">
                                  <Calendar className="h-4 w-4 mr-2" />
                                  {project.acc_created_at ? formatDate(project.acc_created_at) : 'Not available'}
                                </div>
                              </TableCell>
                            );
                          default:
                            return (
                              <TableCell key={column.key}>
                                {project[column.key as keyof Project]}
                              </TableCell>
                            );
                        }
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminProjects;

